import React, { useState, useEffect, useCallback } from 'react';
import { Select, Spin, App } from 'antd';
import tagService from '../../../service/tagService';
import { Tag } from '../../../types/space';

interface TagSelectorProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  placeholder?: string;
}

const TagSelector: React.FC<TagSelectorProps> = ({
  value,
  onChange,
  placeholder = '请选择标签'
}) => {
  const { message } = App.useApp();
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  // 加载标签列表 - 使用useCallback包装
  const loadTags = useCallback(async (search?: string) => {
    setLoading(true);
    try {
      const response = await tagService.getTags({
        keyword: search,
        page: 1,
        pageSize: 100 // 获取较多标签用于选择
      });

      if (response.success && response.data) {
        setTags(response.data);
      } else {
        message.error(response.error || '获取标签列表失败');
      }
    } catch (error) {
      console.error('加载标签失败:', error);
      message.error('加载标签失败');
    } finally {
      setLoading(false);
    }
  }, [message]);

  // 初始化加载
  useEffect(() => {
    loadTags();
  }, [loadTags]);

  // 搜索处理
  const handleSearch = (searchText: string) => {
    setSearchValue(searchText);
    if (searchText.trim()) {
      // 防抖处理
      const timer = setTimeout(() => {
        loadTags(searchText.trim());
      }, 300);
      return () => clearTimeout(timer);
    } else {
      loadTags();
    }
  };

  // 创建选项
  const options = tags.map((tag) => ({
    value: tag.id,
    label: (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {tag.icon ? (
          <div
            style={{
              width: 16,
              height: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            dangerouslySetInnerHTML={{ __html: tag.icon }}
          />
        ) : (
          <div style={{ width: 16, height: 16 }} />
        )}
        <span>{tag.name}</span>
      </div>
    ),
    searchValue: tag.name // 用于搜索
  }));

  return (
    <Select
      mode="multiple"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      options={options}
      loading={loading}
      showSearch
      searchValue={searchValue}
      onSearch={handleSearch}
      filterOption={(input, option) => {
        // 自定义过滤逻辑
        const searchText = input.toLowerCase();
        const optionText = option?.searchValue?.toLowerCase() || '';
        return optionText.includes(searchText);
      }}
      style={{ width: '100%' }}
      maxTagCount="responsive"
      allowClear
      notFoundContent={loading ? <Spin size="small" /> : '暂无标签'}
    />
  );
};

export default TagSelector;
